import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

// ---- Per-org auth-match rules --------------------------------------

export type AuthMatchStatus = "pending" | "verified" | "suspended";

export type AuthMatchView = {
  id: string;
  org_id: string;
  kind: "google_hd" | "microsoft_tid" | "email_domain";
  value: string;
  role: "member" | "manager";
  /** Only a verified rule admits anyone. Absent from older servers. */
  status?: AuthMatchStatus;
  /** How it was proven: dns, microsoft or manual; null while pending. */
  method?: "dns" | "microsoft" | "manual" | null;
  verified_at?: string | null;
  last_checked_at?: string | null;
  /** When the weekly check first missed the DNS record. */
  failing_since?: string | null;
  /** The TXT record that proves a domain (email_domain / google_hd). */
  dns_record_name?: string | null;
  dns_record_value?: string | null;
};

/** The 409 body of a Verify that didn't find the record. */
export type DnsVerifyMiss = {
  code: "dns_record_not_found" | "dns_lookup_failed";
  record_name: string;
  expected: string;
  found: string[];
};

const authMatchKey = (slug: string) =>
  ["orgs", slug, "sso", "auth-match"] as const;

export function useAuthMatchRules(slug: string) {
  return useQuery({
    queryKey: authMatchKey(slug),
    queryFn: async () =>
      (await api.get<AuthMatchView[]>(`/orgs/${slug}/auth-match`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useAddAuthMatchRule(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      kind: string;
      value: string;
      role?: "member" | "manager";
    }) =>
      (await api.post<AuthMatchView>(`/orgs/${slug}/auth-match`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: authMatchKey(slug) }),
  });
}

export function useDeleteAuthMatchRule(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/orgs/${slug}/auth-match/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: authMatchKey(slug) }),
  });
}

/** Look the domain's TXT record up now (409 ``DnsVerifyMiss`` on a miss). */
export function useVerifyAuthMatchRule(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<AuthMatchView>(`/orgs/${slug}/auth-match/${id}/verify`)).data,
    onSettled: () => qc.invalidateQueries({ queryKey: authMatchKey(slug) }),
  });
}

/** Superadmins: mark a claim verified, with a reason for the audit log. */
export function useManualVerifyAuthMatchRule(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; reason: string }) =>
      (
        await api.post<AuthMatchView>(
          `/orgs/${slug}/auth-match/${payload.id}/manual-verify`,
          { reason: payload.reason },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: authMatchKey(slug) }),
  });
}

/** The Microsoft admin-consent URL to send the browser to. */
export function useStartMicrosoftVerification(slug: string) {
  return useMutation({
    mutationFn: async (role: "member" | "manager") =>
      (
        await api.post<{ url: string }>(
          `/orgs/${slug}/auth-match/microsoft/start`,
          { role },
        )
      ).data,
  });
}


// ---- Platform sign-in allow-list (superadmin) ----------------------

export type PlatformAccessEntry = {
  id: string;
  kind: "domain" | "email";
  value: string;
  note: string | null;
  added_by: string;
  created_at: string;
};

export type PlatformAccessMode = {
  mode: "restricted" | "open";
  workspace_domain: string | null;
};

const platformAccessKey = ["admin", "platform", "access-entries"] as const;

export function usePlatformAccessEntries() {
  return useQuery({
    queryKey: platformAccessKey,
    queryFn: async () =>
      (await api.get<PlatformAccessEntry[]>("/admin/platform/access-entries")).data,
    staleTime: 30_000,
  });
}

export function usePlatformAccessMode() {
  return useQuery({
    queryKey: ["admin", "platform", "access-mode"] as const,
    queryFn: async () =>
      (await api.get<PlatformAccessMode>("/admin/platform/access-mode")).data,
    staleTime: 60_000,
  });
}

export function useAddPlatformAccessEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { kind: string; value: string; note?: string }) =>
      (await api.post<PlatformAccessEntry>(
        "/admin/platform/access-entries",
        payload,
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: platformAccessKey }),
  });
}

export function useDeletePlatformAccessEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/admin/platform/access-entries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: platformAccessKey }),
  });
}


// ---- Per-org LDAP --------------------------------------------------

export type OrgLdapConfigView = {
  server_url: string;
  use_starttls: boolean;
  base_dn: string;
  search_attribute: string;
  bind_dn: string | null;
  bind_password_set: boolean;
  /** PEM CA certificate(s) trusted for the org's directory, or null. */
  ca_bundle_pem: string | null;
};

const orgLdapKey = (slug: string) => ["orgs", slug, "sso", "ldap"] as const;

export function useOrgLdapConfig(slug: string) {
  return useQuery({
    queryKey: orgLdapKey(slug),
    queryFn: async () =>
      (await api.get<OrgLdapConfigView | null>(`/orgs/${slug}/ldap`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useSetOrgLdapConfig(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Partial<OrgLdapConfigView> & { bind_password?: string },
    ) =>
      (await api.put<OrgLdapConfigView>(`/orgs/${slug}/ldap`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: orgLdapKey(slug) }),
  });
}
